import {
  Badge,
  Button,
  Flex,
  Group,
  HoverCard,
  LoadingOverlay,
  NumberFormatter,
  NumberInput,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { useProfileStore } from "../../../../../stores/UserStore";
import {
  IconCheck,
  IconDeviceMobile,
  IconInfoCircle,
  IconShoppingCartCheck,
  IconUserSearch,
} from "@tabler/icons-react";
import { parseDateFuture } from "../../../../../functions/dates";
import { IconLinkedin } from "../../../../../components/Icons/IconLinkedin";
import { IconEmail } from "../../../../../assets/icons/IconEmail";
import TrialElementError from "./TrialElementError";

const TrialElement = ({
  loading,
  reccurence,
  setRecurrence,
  linkedin,
  setLinkedin,
  email,
  setEmail,
  enrich,
  setEnrich,
  mobile,
  setMobile,
  price,
  handleCheckout,
}) => {
  const lang = useLanguage((s) => s.language);
  const profile = useProfileStore((s) => s.profile);

  return (
    <Stack gap={0} mt={16}>
      <TrialElementError subscriptions={profile?.subscriptions} />

      <Title order={4} ml={"xl"} mb={16}>
        {lang.settings.actual_plan}
      </Title>

      <Paper
        p={"xl"}
        component={Stack}
        w={"100%"}
        justify="center"
        pos={"relative"}
        style={{ overflow: "hidden" }}
        mb={32}
      >
        <Title order={4} c={"primary"}>
          {lang.pricing.free.title}
        </Title>

        <Flex bg={"red.2"} p={"md"} style={{ borderRadius: 8 }}>
          <Text size="md">
            {profile?.subscriptions?.last_payment_succeeded === false &&
            profile?.subscriptions?.subscription_id ? (
              lang.pricing.free.ended_payment_error
            ) : profile?.subscriptions?.end_date ? (
              new Date(profile?.subscriptions?.end_date) < new Date() ? (
                lang.pricing.free.ended
              ) : (
                <>
                  {lang.pricing.free.will_end}{" "}
                  <span style={{ fontWeight: 500 }}>
                    {parseDateFuture(profile?.subscriptions?.end_date)}
                  </span>
                </>
              )
            ) : (
              <>{lang.pricing.free.ended}</>
            )}
          </Text>
        </Flex>

        {profile?.subscriptions?.end_date &&
        new Date(profile?.subscriptions?.end_date) < new Date() ? (
          ""
        ) : (
          <Group justify="space-between">
            {Object.keys(lang.pricing.free.features).map((key, i) => {
              return (
                <Group key={i} w={"40%"}>
                  <ThemeIcon radius={"md"} variant="filled" color="green">
                    <IconCheck size={20} />
                  </ThemeIcon>

                  <Text fw={500}>{lang.pricing.free.features[key]}</Text>
                </Group>
              );
            })}
          </Group>
        )}
      </Paper>

      <Title order={3} ml={"xl"} mb={8} ta={"center"}>
        {lang.settings.trial_title}
      </Title>
      <Paper
        p={"lg"}
        component={Stack}
        radius={"lg"}
        mt={"sm"}
        w={"100%"}
        maw={850}
        mx={"auto"}
        justify="center"
        // style={{ border: "2px solid var(--mantine-color-secondary-6)" }}
        style={{ boxShadow: "#00000012 0 2px 6px" }}
        pos={"relative"}
      >
        <LoadingOverlay visible={loading} style={{ borderRadius: 24 }} />

        <Title order={4} ta={"center"}>
          {lang.pricing.title}
        </Title>
        {/* <Text ta={"center"}>{lang.pricing.title_tooltip}</Text> */}
        {/* <Title order={4} ta={"center"}>
          {lang.pricing.premium.title}
        </Title> */}

        <SegmentedControl
          data={[
            {
              value: "monthly",
              label: (
                <Group w={"100%"} justify="center">
                  <Text fw={500}>{lang.pricing.monthly}</Text>
                </Group>
              ),
            },
            {
              value: "annual",
              label: (
                <Group w={"100%"} justify="center" pos={"relative"}>
                  <Text fw={500}>{lang.pricing.annual}</Text>

                  <Badge color="primary" size="md" mr={-18}>
                    -20%
                  </Badge>
                </Group>
              ),
            },
          ]}
          w={600}
          mx={"auto"}
          size="lg"
          value={reccurence}
          onChange={setRecurrence}
        />

        <Group gap={"xl"} mt={16}>
          <Stack gap={"xl"}>
            <Group
              wrap="nowrap"
              gap={"xs"}
              bg={"gray.1"}
              style={{ borderRadius: 12 }}
              p={"md"}
            >
              <Stack w={250} gap={4}>
                <Group>
                  <ThemeIcon variant="subtle">
                    <IconLinkedin size={24} />
                  </ThemeIcon>
                  <Text mr={8} fw={600}>
                    {lang.pricing.linkedin_accounts}
                  </Text>
                </Group>
              </Stack>
              <NumberInput
                value={linkedin}
                onChange={(v) => {
                  setLinkedin(v);
                }}
                w={100}
                ml={"auto"}
                min={0}
                max={100}
                size="md"
              />
            </Group>

            <Group
              wrap="nowrap"
              gap={"xs"}
              bg={"gray.1"}
              style={{ borderRadius: 12 }}
              p={"md"}
            >
              <Stack w={250} gap={4}>
                <Group>
                  <ThemeIcon color="orange" variant="subtle">
                    <IconEmail size={24} />
                  </ThemeIcon>
                  <Text mr={8} fw={600}>
                    {lang.pricing.emails}
                  </Text>
                </Group>
                <Text size="xs" c={"dimmed"}>
                  {lang.pricing.premium.email_desc}
                </Text>
              </Stack>
              <NumberInput
                value={email * 5}
                onChange={(v) => {
                  setEmail(v / 5);
                }}
                onBlur={(e) => {
                  let v = parseInt(e.target.value);

                  setEmail(Math.ceil(v / 5));
                }}
                step={5}
                w={100}
                ml={"auto"}
                min={0}
                max={100}
                size="md"
              />
            </Group>

            <Group
              wrap="nowrap"
              gap={"xs"}
              bg={"gray.1"}
              style={{ borderRadius: 12 }}
              p={"md"}
            >
              <Stack w={300} gap={4}>
                <Group>
                  <ThemeIcon color="tertiary.8" variant="subtle">
                    <IconUserSearch size={24} />
                  </ThemeIcon>
                  <Text mr={8} fw={600}>
                    {lang.pricing.enrich_monthly}
                  </Text>

                  <EnrichInfo />
                </Group>
                <Text size="xs" c={"dimmed"}>
                  {lang.pricing.enrich_text}
                </Text>
              </Stack>
              <NumberInput
                value={enrich * 1000}
                onChange={(v) => {
                  setEnrich(v / 1000);
                }}
                w={100}
                size="md"
                ml={"auto"}
                step={1000}
                min={0}
                max={100000}
                onBlur={(e) => {
                  let v = parseInt(e.target.value);

                  setEnrich(Math.ceil(v / 1000));
                }}
              />
            </Group>

            <Group
              wrap="nowrap"
              gap={"xs"}
              bg={"gray.1"}
              style={{ borderRadius: 12 }}
              p={"md"}
            >
              <Stack w={300} gap={4}>
                <Group>
                  <ThemeIcon color="tertiary.8" variant="subtle">
                    <IconDeviceMobile size={24} />
                  </ThemeIcon>
                  <Text mr={8} fw={600}>
                    {lang.settings.mobile_phone_per_month}
                  </Text>
                </Group>
                <Text size="xs" c={"dimmed"}>
                  {lang.settings.mobile_phone_text}
                </Text>
              </Stack>
              <NumberInput
                value={mobile * 100}
                onChange={(v) => {
                  setMobile(v / 100);
                }}
                w={100}
                size="md"
                ml={"auto"}
                step={100}
                min={0}
                max={100000}
                onBlur={(e) => {
                  let v = parseInt(e.target.value);

                  setMobile(Math.ceil(v / 1000));
                }}
              />
            </Group>
          </Stack>

          <Stack ml={"auto"} mr={16}>
            {lang.pricing.premium.features.map((item, i) => (
              <Group key={i}>
                <ThemeIcon radius={"md"} variant="filled" color="green">
                  <IconCheck size={20} />
                </ThemeIcon>

                <Text>{item}</Text>
              </Group>
            ))}
          </Stack>
        </Group>

        <Group mt={8}>
          <Text size="xl" c={"primary"}>
            <b style={{ fontSize: 64, marginRight: 8 }}>
              <NumberFormatter value={price?.toFixed(0)} />€
            </b>{" "}
            {reccurence === "monthly"
              ? lang.pricing.monthly_payment
              : lang.pricing.annual_payment}
          </Text>

          <Button
            size="xl"
            variant="gradient"
            leftSection={<IconShoppingCartCheck size={32} />}
            ml={"auto"}
            w={250}
            fw={700}
            onClick={handleCheckout}
            disabled={linkedin + email + enrich + mobile === 0}
          >
            {lang.pricing.next}
          </Button>
        </Group>
      </Paper>
    </Stack>
  );
};

const EnrichInfo = () => {
  const lang = useLanguage((s) => s.language);

  return (
    <HoverCard position="top">
      <HoverCard.Target>
        <ThemeIcon variant="subtle">
          <IconInfoCircle />
        </ThemeIcon>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <Stack maw={300}>
          <Text>{lang.pricing.premium.enrich_info}</Text>
          <Text>{lang.pricing.premium.enrich_info2}</Text>
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};

export default TrialElement;
